import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, of, tap } from 'rxjs';

interface TermsStatusResponse {
  required: boolean;
  isUpdate: boolean;
  isAlliantUser: boolean;
}

const TERMS_STATUS_URL = '/api/terms-of-use/status';
const TERMS_ACCEPT_URL = '/api/terms-of-use/accept';

/**
 * Service responsible for checking and recording users' Terms of Use acceptance.
 *
 * All portal types (admin, student, institution) share the same backend endpoints.
 * The server-side logic determines the appropriate acceptance rules based on the
 * authenticated user's type. The acceptance state is cached for the duration of the
 * Angular session. After the user accepts, the cached state is updated so no additional
 * API call is needed on the next guard evaluation.
 */
@Injectable({ providedIn: 'root' })
export class TermsAcceptanceService {
  private readonly http = inject(HttpClient);

  /** Cached acceptance-required state. `null` means not yet checked. */
  private readonly _required = signal<boolean | null>(null);

  /**
   * Whether the current acceptance requirement is due to updated terms (re-accept)
   * rather than a first-time acceptance. Cached alongside `_required`.
   */
  readonly isUpdate = signal(false);

  /**
   * Whether the current user is an Alliant admin user (no institution).
   * Cached alongside `_required` from the status endpoint response.
   */
  readonly isAlliantUser = signal(false);

  /**
   * Checks whether the current user is required to accept the Terms of Use.
   * The result is cached for the duration of the Angular session.
   */
  checkRequired(): Observable<boolean> {
    const cached = this._required();
    if (cached !== null) {
      return of(cached);
    }

    return this.http.get<TermsStatusResponse>(TERMS_STATUS_URL).pipe(
      tap((response) => {
        this._required.set(response.required);
        this.isUpdate.set(response.isUpdate);
        this.isAlliantUser.set(response.isAlliantUser);
      }),
      map((response) => response.required)
    );
  }

  /**
   * Records the current user's acceptance of the Terms of Use via the backend API.
   * On success, updates the cached state so subsequent guard checks pass without another API call.
   */
  accept(): Observable<void> {
    return this.http.post<void>(TERMS_ACCEPT_URL, {}).pipe(
      tap(() => {
        this._required.set(false);
        this.isUpdate.set(false);
      })
    );
  }

  /**
   * Marks the Terms of Use as required in the cache without making an API call.
   * Used when an HTTP interceptor intercepts a `terms-acceptance-required` error:
   * setting the cache directly avoids a follow-up request to the status endpoint.
   */
  markRequired(): void {
    this._required.set(true);
  }

  /**
   * Resets the cached acceptance state, forcing a re-check on the next guard evaluation.
   * Call this after logout.
   */
  reset(): void {
    this._required.set(null);
    this.isUpdate.set(false);
    this.isAlliantUser.set(false);
  }
}
